import type { ReactNode, RefObject } from 'react';
import { useLayoutEffect, useRef } from 'react';
import { useOutlet } from 'react-router-dom';

interface AdminCachedOutletProps {
  activeKey: string;
  cacheKeys: string[];
  scrollContainerRef: RefObject<HTMLElement | null>;
}

/**
 * 保留已打开页签的组件实例。切换页签时只隐藏旧页面，关闭页签时才卸载。
 */
export function AdminCachedOutlet({
  activeKey,
  cacheKeys,
  scrollContainerRef,
}: AdminCachedOutletProps) {
  const outlet = useOutlet();
  const outletCacheRef = useRef(new Map<string, ReactNode>());
  const scrollPositionsRef = useRef(new Map<string, number>());
  const cacheable = cacheKeys.includes(activeKey);

  useLayoutEffect(() => {
    if (cacheable && outlet && !outletCacheRef.current.has(activeKey)) {
      outletCacheRef.current.set(activeKey, outlet);
    }

    const retainedKeys = new Set(cacheKeys);
    outletCacheRef.current.forEach((_, key) => {
      if (!retainedKeys.has(key)) outletCacheRef.current.delete(key);
    });
    scrollPositionsRef.current.forEach((_, key) => {
      if (!retainedKeys.has(key)) scrollPositionsRef.current.delete(key);
    });
  }, [activeKey, cacheKeys, cacheable, outlet]);

  useLayoutEffect(() => {
    const container = scrollContainerRef.current;
    if (!container) return undefined;

    container.scrollTop = scrollPositionsRef.current.get(activeKey) ?? 0;
    return () => {
      scrollPositionsRef.current.set(activeKey, container.scrollTop);
    };
  }, [activeKey, scrollContainerRef]);

  const renderedKeys = cacheable
    ? cacheKeys
    : [...cacheKeys, activeKey];
  const cachedActiveOutlet = outletCacheRef.current.get(activeKey);

  return renderedKeys.map(key => {
    const content = key === activeKey
      ? (cachedActiveOutlet ?? outlet)
      : outletCacheRef.current.get(key);
    if (!content) return null;
    const active = key === activeKey;
    return (
      <div
        key={key}
        className="admin-cached-page"
        hidden={!active}
        aria-hidden={!active}
      >
        {content}
      </div>
    );
  });
}
